import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../platform/AuthProvider";
import { api } from "../api";

const MANUAL_SOURCE_KEY = "outclose_lang_source";

/**
 * Returns "manual" if the user has explicitly chosen a language via the
 * LanguageSelector at any point, otherwise "auto" (i18next-browser-languagedetector
 * picked it from navigator/localStorage).
 */
function getLanguageSource(): "auto" | "manual" {
  try {
    return localStorage.getItem(MANUAL_SOURCE_KEY) === "manual" ? "manual" : "auto";
  } catch {
    return "auto";
  }
}

/**
 * Split a BCP-47 locale tag into a primary language code and an optional region.
 *
 *   "en"     -> { language: "en" }
 *   "en-US"  -> { language: "en", region: "US" }
 *   "pt-BR"  -> { language: "pt", region: "BR" }
 */
function splitLocale(tag: string): { language: string; region?: string } {
  const parts = tag.split("-");
  return {
    language: (parts[0] || "").toLowerCase(),
    region: parts[1] ? parts[1].toUpperCase() : undefined,
  };
}

/**
 * Keep Customer.io informed about the user's active UI language.
 *
 * Fires `POST /api/user/locale` once after auth is ready and again any time
 * the user changes language at runtime (e.g. via LanguageSelector). The
 * server forwards the values to CIO via `identifyUser`, which is idempotent
 * — calling repeatedly with the same values is cheap and safe.
 *
 * Sends three signals so lifecycle marketing can distinguish between what
 * the user sees in-app and what their device prefers:
 *   - `language` — the active i18n locale (what the UI is rendering in)
 *   - `device_locale` — `navigator.language` (browser/OS preference)
 *   - `language_source` — "manual" if the user picked it, else "auto"
 */
export function useLanguageSync(): void {
  const { i18n } = useTranslation();
  const { isLoaded, userId } = useAuth();
  // Avoid duplicate sends for the same locale during a single session.
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !userId) {
      // Reset cache when the user signs out so the next sign-in re-syncs.
      lastSentRef.current = null;
      return;
    }

    const sync = async (activeLanguage: string) => {
      const deviceLocale =
        typeof navigator !== "undefined" ? navigator.language : undefined;
      const source = getLanguageSource();
      // Dedupe identical (language, deviceLocale, source) tuples.
      const fingerprint = `${activeLanguage}|${deviceLocale ?? ""}|${source}`;
      if (lastSentRef.current === fingerprint) return;
      lastSentRef.current = fingerprint;

      const { language, region } = splitLocale(activeLanguage);
      if (!language) return;

      try {
        await api.post("/user/locale", {
          language,
          ...(region ? { language_region: region } : {}),
          ...(deviceLocale ? { device_locale: deviceLocale } : {}),
          language_source: source,
        });
      } catch (err) {
        // Non-critical: don't block the app on a marketing-attribute write.
        console.warn("[i18n] Failed to sync language to Customer.io:", err);
      }
    };

    // Fire on mount with whatever language is currently active.
    void sync(i18n.language);

    // And again any time the user switches language (e.g. via LanguageSelector).
    const onChange = (lng: string) => {
      void sync(lng);
    };
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, [isLoaded, userId, i18n]);
}

/**
 * Internal helper used by `LanguageSelector` to record that the user has
 * explicitly chosen a language. Subsequent sync calls will report
 * `language_source: "manual"` until localStorage is cleared.
 */
export function markLanguageSourceManual(): void {
  try {
    localStorage.setItem(MANUAL_SOURCE_KEY, "manual");
  } catch {
    // ignore (private mode, quota, etc.)
  }
}
